/**
 * ==== STANDALONE:FARMHAND_PRO:START ====
 * Feature: Farm Mapping & Pasture Tracking API Service
 * Tier: Pro ($7/mo)
 * Dependencies: axios, api.ts
 * Generalization: Update API base URL for standalone
 * ============================================
 */

import api from './api';

// Types
export interface FarmMap {
  id: number;
  center_lat: string;
  center_lon: string;
  default_zoom: number;
  total_acreage: string | null;
  total_grazable_acreage: string | null;
  tile_bounds_ne_lat: string | null;
  tile_bounds_ne_lon: string | null;
  tile_bounds_sw_lat: string | null;
  tile_bounds_sw_lon: string | null;
  created_at: string;
  updated_at: string;
  zones: FarmZoneListItem[];
}

export interface FarmZoneListItem {
  id: number;
  name: string;
  zone_type: string;
  zone_type_display: string;
  geometry_type: 'polygon' | 'line' | 'point';
  acreage: string | null;
  grazable_acres: string | null;
  color: string;
  is_active: boolean;
}

export interface FarmZone extends FarmZoneListItem {
  geometry: GeoJSONPolygon | GeoJSONLineString | { type: 'Point'; coordinates: number[] };
  grazable_percent: number;
  notes: string;
  rest_days_poultry: number;
  rest_days_cattle: number;
  rest_days_sheep_goat: number;
  rest_days_pigs: number;
  rest_days_rabbits: number;
  rest_days_turkeys: number;
  created_at: string;
  updated_at: string;
  rest_statuses: Record<string, ZoneRestStatusData>;
}

export interface ZoneRestStatusData {
  days_since_grazed: number;
  required_rest_days: number;
  is_ready: boolean;
  ready_date: string | null;
  last_grazed_date: string;
}

export interface GeoJSONPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

export interface MobileShelter {
  id: number;
  name: string;
  shelter_type: string;
  shelter_type_display: string;
  length_ft: string;
  width_ft: string;
  sq_ft: string;
  bird_capacity: number | null;
  animal_capacity: number | null;
  units_in_group: number;
  group_total_sq_ft: string;
  notes: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SessionMovement {
  from_lat: number | null;
  from_lon: number | null;
  to_lat: number | null;
  to_lon: number | null;
  moved_at: string | null;
  notes: string;
}

export interface GrazingSession {
  id: number;
  zone: number;
  zone_name: string;
  batch: string;  // UUID of AnimalBatch
  batch_name: string;
  plan_id: number | null;  // SeasonalPlan ID for Farmhand URL
  shelter: number | null;
  shelter_name: string | null;
  // Shelter dimensions for map visualization
  shelter_length_ft: string | null;
  shelter_width_ft: string | null;
  shelter_type: string | null;
  shelter_units_in_group: number | null;  // Max shelters available
  started_at: string;
  ended_at: string | null;
  is_active: boolean;
  // Shelter position tracking
  start_lat: string | null;
  start_lon: string | null;
  current_lat: string | null;
  current_lon: string | null;
  heading: string | null;  // Direction of travel in degrees
  // Multi-shelter formation
  shelter_count: number;  // Number of shelters deployed
  formation_heading: string | null;  // Initial direction set during placement
  animal_count: number;
  animal_type: string;
  days_grazed: number | null;
  breadcrumb_count: number;
  area_covered_sq_ft: string | null;
  area_covered_percent: string | null;
  avg_density_per_acre: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
  // Movement history for breadcrumb trail
  movements?: SessionMovement[];
}

export interface GrazingBreadcrumb {
  id: number;
  grazing_session: number;
  latitude: string;
  longitude: string;
  accuracy_meters: string | null;
  logged_at: string;
  logged_via: string;
  logged_via_display: string;
  movement_type: 'grazing' | 'transit';
  movement_type_display: string;
  notes: string;
  is_deleted: boolean;
}

export interface TracedPaddock {
  id: number;
  grazing_session: number;
  name: string;
  sequence_number: number;
  geometry: GeoJSONPolygon;
  trace_started_at: string;
  trace_ended_at: string;
  area_sq_ft: string;
  perimeter_ft: string;
  animal_count: number;
  density_per_acre: string;
  vacated_at: string | null;
  days_occupied: number | null;
  notes: string;
}

export interface ZoneRestStatus {
  id: number;
  zone: number;
  zone_name: string;
  animal_type: string;
  last_grazed_date: string;
  batch_name: string | null;
  days_since_grazed: number;
  required_rest_days: number;
  is_ready: boolean;
  ready_date: string | null;
}

export interface StackingPlan {
  id: number;
  zone: number;
  zone_name: string;
  name: string;
  year: number;
  rotation_sequence: RotationStep[];
  notes: string;
  is_active: boolean;
}

export interface MovementPlan {
  id: number;
  name: string;
  description: string;
  batch: string | null;  // UUID
  batch_name: string | null;
  path_geometry: GeoJSONLineString | GeoJSONPolygon | null;
  tractor_width_ft: string | null;
  tractor_length_ft: string | null;
  num_tractors: number;
  estimated_coverage_sq_ft: string | null;
  is_active: boolean;
  start_date: string | null;
  color: string;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
}

export interface MovementPlanListItem {
  id: number;
  name: string;
  batch_name: string | null;
  is_active: boolean;
  color: string;
  start_date: string | null;
  estimated_coverage_sq_ft: string | null;
  created_at: string;
}

export interface GeoJSONLineString {
  type: 'LineString';
  coordinates: number[][];
}

export interface RotationStep {
  species: string;
  batch_id: number | null;
  days_behind_previous: number;
}

export interface PastureUtilizationReport {
  period: {
    start: string;
    end: string;
  };
  summary: {
    total_grazable_acres: number;
    zones_grazed: number;
    total_grazing_days: number;
    total_animal_days: number;
  };
  zones: ZoneUtilization[];
}

export interface ZoneUtilization {
  name: string;
  acreage: number;
  grazing_days: number;
  animal_days: number;
  utilization_percent: number;
  sessions: number;
}

export interface PlannerBatch {
  id: string;
  batch_number: string;
  customer_batch_id: string;
  profile_name: string;
  profile_category: string;
  quantity_started: number;
  arrival_date: string;
  status: string;
  status_display: string;
  current_week: number | null;
  expected_harvest_date: string | null;
  custom_name?: string;
  plan_id?: number;
  plan_name?: string;
}

export interface CloudBackupConfig {
  version: number;
  saved_at: string;
  farm_map: {
    id: number;
    center_lat: number;
    center_lon: number;
    default_zoom: number;
    total_acreage: number;
    total_grazable_acreage: number;
    tile_bounds: {
      sw_lat: number | null;
      sw_lon: number | null;
      ne_lat: number | null;
      ne_lon: number | null;
    };
    tiles_min_zoom: number | null;
    tiles_max_zoom: number | null;
  };
  zones: Array<{
    id: number;
    name: string;
    zone_type: string;
    color: string;
    geometry: GeoJSONPolygon;
    acreage: number | null;
    grazable_acres: number | null;
    rest_days_cattle: number;
    rest_days_poultry: number;
    rest_days_sheep_goat: number;
    rest_days_pigs: number;
    rest_days_rabbits: number;
    rest_days_turkeys: number;
    notes: string;
  }>;
  shelters: Array<{
    id: number;
    name: string;
    shelter_type: string;
    length_ft: number;
    width_ft: number;
    sq_ft: number | null;
    bird_capacity: number | null;
    animal_capacity: number | null;
    units_in_group: number;
    group_total_sq_ft: number | null;
    notes: string;
  }>;
}

// Historical Map Timeline Types
export interface TimelineBatch {
  id: string;
  name: string;
  animal_type: string;
  quantity: number;
  start_date: string | null;
  end_date: string | null;
  status: string;
}

export interface PaddockSnapshot {
  id: number;
  batch_id: string;
  coordinates: GeoJSONPolygon['coordinates'];
  shelter_coordinates: {
    lat: number;
    lon: number;
    heading?: number;
    formation_heading?: number;
  } | null;
  date_started: string;
  date_ended: string | null;
  is_current: boolean;
}

export interface TimelineEvent {
  id: number;
  date: string;
  time: string | null;
  event_type: string;
  title: string;
  icon: string;
  severity: 'info' | 'warning' | 'critical';
  coordinates: { lat: number; lon: number } | null;
  batch_id: string | null;
}

export interface TimelineStatistics {
  total_events: number;
  event_breakdown: Record<string, number>;
  total_paddock_snapshots: number;
}

export interface TimelineData {
  year: number;
  date_range: {
    start: string;
    end: string;
  };
  batches: TimelineBatch[];
  paddock_snapshots: PaddockSnapshot[];
  events: TimelineEvent[];
  statistics: TimelineStatistics;
}

export interface LauraFarmContext {
  has_farm_map: boolean;
  total_acreage?: number;
  grazable_acreage?: number;
  zones: Array<{
    id: number;
    name: string;
    acreage: number;
    grazable_acres: number;
    rest_status: Record<string, {
      days_since_grazed: number;
      required_days: number;
      is_ready: boolean;
      ready_date: string | null;
    }>;
  }>;
  active_sessions: Array<{
    batch_name: string;
    zone_name: string;
    animal_count: number;
    animal_type: string;
    days_in_zone: number;
    coverage_percent: number;
  }>;
  recent_movements: Array<{
    batch: string;
    zone: string;
    logged_at: string;
    movement_type: string;
    notes: string;
  }>;
  shelters: Array<{
    name: string;
    type: string;
    sq_ft: number;
    units: number;
    total_sq_ft: number;
  }>;
  stacking_plans: Array<{
    name: string;
    zone: string;
    sequence: RotationStep[];
  }>;
}

export interface FarmIncident {
  id: number;
  customer: number;
  date: string;
  time: string | null;
  event_type: string;
  event_type_display: string;
  title: string;
  description: string;
  batch: string | null;
  batch_name: string | null;
  zone: number | null;
  zone_name: string | null;
  coordinates: { lat: number; lon: number } | null;
  severity: 'info' | 'warning' | 'critical';
  severity_display: string;
  icon: string;
  count: number | null;
  resolved: boolean;
  resolved_at: string | null;
  resolved_by: number | null;
  resolved_by_name: string | null;
  resolved_notes: string;
  photo: string | null;
  reported_by_name: string | null;
  season_year: number | null;
  source_model: string;
  created_at: string;
  updated_at: string;
}

export interface PatternCluster {
  center: { lat: number; lon: number };
  radius_meters: number;
  event_count: number;
  severity: string;
  pattern_type: string;
  date_range: { start: string; end: string };
  events: Array<{ id: number; title: string; date: string }>;
}

export interface MovementPlanAdvisory {
  plan_id: number;
  plan_name: string;
  overall_risk: 'high' | 'medium' | 'low';
  warnings: Array<{ message: string; severity: string }>;
  recommendations: Array<{ message: string; priority: string; action?: string }>;
  positive_notes: string[];
  positives: string[];
  zone_assessments: Array<{
    zone_id: number;
    zone_name: string;
    rest_status: string;
    incident_count: number;
    risk_level: string;
  }>;
  zone_evaluations: Array<{
    zone_id: number;
    zone_name: string;
    warnings: Array<{ type: string; message: string; severity: string }>;
  }>;
  pattern_warnings: Array<{ message: string }>;
}

// API Service
export const farmMapAPI = {
  // Farm Map
  getFarmMap: async (): Promise<{ exists: boolean; farm_map?: FarmMap }> => {
    const response = await api.get('/farmmap/');
    return response.data;
  },

  initializeFarmMap: async (latitude: number, longitude: number): Promise<FarmMap> => {
    const response = await api.post('/farmmap/initialize/', { latitude, longitude });
    return response.data;
  },

  // Zones
  getZones: async (): Promise<FarmZoneListItem[]> => {
    const response = await api.get('/farmmap/zones/');
    // Handle paginated response (returns { results: [...] }) or direct array
    const data = response.data;
    return Array.isArray(data) ? data : (data?.results || []);
  },

  getZone: async (id: number): Promise<FarmZone> => {
    const response = await api.get(`/farmmap/zones/${id}/`);
    return response.data;
  },

  createZone: async (data: Partial<FarmZone>): Promise<FarmZone> => {
    const response = await api.post('/farmmap/zones/', data);
    return response.data;
  },

  updateZone: async (id: number, data: Partial<FarmZone>): Promise<FarmZone> => {
    const response = await api.patch(`/farmmap/zones/${id}/`, data);
    return response.data;
  },

  deleteZone: async (id: number): Promise<void> => {
    await api.delete(`/farmmap/zones/${id}/`);
  },

  // Shelters
  getShelters: async (): Promise<MobileShelter[]> => {
    const response = await api.get('/farmmap/shelters/');
    // Handle paginated response (returns { results: [...] }) or direct array
    const data = response.data;
    return Array.isArray(data) ? data : (data?.results || []);
  },

  getShelter: async (id: number): Promise<MobileShelter> => {
    const response = await api.get(`/farmmap/shelters/${id}/`);
    return response.data;
  },

  createShelter: async (data: Partial<MobileShelter>): Promise<MobileShelter> => {
    const response = await api.post('/farmmap/shelters/', data);
    return response.data;
  },

  updateShelter: async (id: number, data: Partial<MobileShelter>): Promise<MobileShelter> => {
    const response = await api.patch(`/farmmap/shelters/${id}/`, data);
    return response.data;
  },

  deleteShelter: async (id: number): Promise<void> => {
    await api.delete(`/farmmap/shelters/${id}/`);
  },

  // Sessions
  getSessions: async (params?: { active?: boolean; zone_id?: number }): Promise<GrazingSession[]> => {
    const response = await api.get('/farmmap/sessions/', { params });
    // Handle paginated response (returns { results: [...] }) or direct array
    const data = response.data;
    return Array.isArray(data) ? data : (data?.results || []);
  },

  getSession: async (id: number): Promise<GrazingSession & { breadcrumbs: GrazingBreadcrumb[] }> => {
    const response = await api.get(`/farmmap/sessions/${id}/`);
    return response.data;
  },

  startSession: async (data: {
    zone_id: number;
    batch_id?: string;  // AnimalBatch uses UUID, optional
    shelter_id?: number;
    animal_count: number;
    animal_type: string;
    notes?: string;
    start_lat?: number;  // Initial shelter position (click-to-place)
    start_lon?: number;
    shelter_count?: number;  // Number of shelters to deploy
    formation_heading?: number;  // Initial direction
  }): Promise<GrazingSession> => {
    const response = await api.post('/farmmap/sessions/', data);
    return response.data;
  },

  endSession: async (id: number): Promise<GrazingSession> => {
    const response = await api.post(`/farmmap/sessions/${id}/end/`);
    return response.data;
  },

  updateSession: async (id: number, data: {
    animal_count?: number;
    shelter_count?: number;
    notes?: string;
  }): Promise<GrazingSession> => {
    const response = await api.patch(`/farmmap/sessions/${id}/`, data);
    return response.data;
  },

  updateSessionPosition: async (
    sessionId: number,
    start_lat: number,
    start_lon: number,
    heading?: number,  // Direction of travel
    formation_heading?: number  // Line rotation angle
  ): Promise<GrazingSession> => {
    const response = await api.post(
      `/farmmap/sessions/${sessionId}/update_position/`,
      { start_lat, start_lon, heading, formation_heading }
    );
    return response.data;
  },

  // Breadcrumbs / Movement Logging
  logMovement: async (data: {
    session_id: number;
    latitude: number;
    longitude: number;
    accuracy_meters?: number;
    movement_type?: 'grazing' | 'transit';
    logged_via?: string;
    notes?: string;
    voice_note_url?: string;
  }): Promise<GrazingBreadcrumb> => {
    const response = await api.post('/farmmap/log-movement/', data);
    return response.data;
  },

  deleteBreadcrumb: async (id: number): Promise<void> => {
    await api.post(`/farmmap/breadcrumbs/${id}/soft_delete/`);
  },

  restoreBreadcrumb: async (id: number): Promise<GrazingBreadcrumb> => {
    const response = await api.post(`/farmmap/breadcrumbs/${id}/restore/`);
    return response.data;
  },

  // Paddock Tracing
  startTrace: async (sessionId: number): Promise<{ paddock_id: number; message: string }> => {
    const response = await api.post('/farmmap/start-trace/', { session_id: sessionId });
    return response.data;
  },

  addTracePoint: async (data: {
    paddock_id: number;
    latitude: number;
    longitude: number;
    accuracy_meters?: number;
  }): Promise<{ id: number; sequence: number }> => {
    const response = await api.post('/farmmap/add-trace-point/', data);
    return response.data;
  },

  endTrace: async (
    paddockId: number,
    coordinates?: [number, number][],  // Optional: [[lat, lon], ...] collected client-side
    coordinateFormat: 'latlon' | 'lonlat' = 'latlon'
  ): Promise<TracedPaddock> => {
    const data: {
      paddock_id: number;
      coordinates?: number[][];
      coordinate_format?: string;
    } = { paddock_id: paddockId };

    if (coordinates && coordinates.length >= 3) {
      data.coordinates = coordinates;
      data.coordinate_format = coordinateFormat;
    }

    const response = await api.post('/farmmap/end-trace/', data);
    return response.data;
  },

  vacatePaddock: async (id: number): Promise<TracedPaddock> => {
    const response = await api.post(`/farmmap/traced-paddocks/${id}/vacate/`);
    return response.data;
  },

  getPaddockWaterNeeds: async (paddockId: number): Promise<{
    paddock_id: number;
    paddock_name: string;
    water_needs: {
      animal_count: number;
      paddock_area_sq_ft: number;
      paddock_area_acres: number;
      base_daily_need_gallons: number;
      weather_adjusted_need_gallons: number;
      weather_multiplier: number;
      recommended_capacity_gallons: number;
      min_waterers_by_consumption: number;
      min_waterers_by_spacing: number;
      recommended_waterers: number;
      spacing_advice: string;
      paddock_tip: string;
      weather_risk_level: string;
      weather_alerts: string[];
    };
    weather_risk: {
      risk_level: string;
      water_multiplier: number;
    };
    key_messages: string[];
  }> => {
    const response = await api.get(`/farmmap/traced-paddocks/${paddockId}/water_needs/`);
    return response.data;
  },

  // Rest Status
  getRestStatuses: async (animalType?: string): Promise<ZoneRestStatus[]> => {
    const params = animalType ? { animal_type: animalType } : undefined;
    const response = await api.get('/farmmap/rest-status/', { params });
    // Handle paginated response (returns { results: [...] }) or direct array
    const data = response.data;
    return Array.isArray(data) ? data : (data?.results || []);
  },

  getReadyZones: async (animalType: string): Promise<Array<{
    zone_id: number;
    name: string;
    acreage: number;
    days_rested: number | null;
    last_grazed: string | null;
    note?: string;
  }>> => {
    const response = await api.get('/farmmap/ready-zones/', { params: { animal_type: animalType } });
    // Handle paginated response (returns { results: [...] }) or direct array
    const data = response.data;
    return Array.isArray(data) ? data : (data?.results || []);
  },

  // Stacking Plans
  getStackingPlans: async (): Promise<StackingPlan[]> => {
    const response = await api.get('/farmmap/stacking-plans/');
    // Handle paginated response (returns { results: [...] }) or direct array
    const data = response.data;
    return Array.isArray(data) ? data : (data?.results || []);
  },

  createStackingPlan: async (data: Partial<StackingPlan>): Promise<StackingPlan> => {
    const response = await api.post('/farmmap/stacking-plans/', data);
    return response.data;
  },

  updateStackingPlan: async (id: number, data: Partial<StackingPlan>): Promise<StackingPlan> => {
    const response = await api.patch(`/farmmap/stacking-plans/${id}/`, data);
    return response.data;
  },

  // Movement Plans
  getMovementPlans: async (): Promise<MovementPlanListItem[]> => {
    const response = await api.get('/farmmap/movement-plans/');
    const data = response.data;
    return Array.isArray(data) ? data : (data?.results || []);
  },

  getActiveMovementPlans: async (): Promise<MovementPlan[]> => {
    const response = await api.get('/farmmap/movement-plans/active/');
    return response.data;
  },

  getMovementPlan: async (id: number): Promise<MovementPlan> => {
    const response = await api.get(`/farmmap/movement-plans/${id}/`);
    return response.data;
  },

  createMovementPlan: async (data: {
    name: string;
    description?: string;
    batch?: string;  // UUID
    path_geometry?: GeoJSONLineString | GeoJSONPolygon;
    tractor_width_ft?: number;
    tractor_length_ft?: number;
    num_tractors?: number;
    start_date?: string;
    color?: string;
  }): Promise<MovementPlan> => {
    const response = await api.post('/farmmap/movement-plans/', data);
    return response.data;
  },

  updateMovementPlan: async (id: number, data: Partial<{
    name: string;
    description: string;
    batch: string | null;
    path_geometry: GeoJSONLineString | GeoJSONPolygon | null;
    tractor_width_ft: number | null;
    tractor_length_ft: number | null;
    num_tractors: number;
    estimated_coverage_sq_ft: number | null;
    is_active: boolean;
    start_date: string | null;
    color: string;
  }>): Promise<MovementPlan> => {
    const response = await api.patch(`/farmmap/movement-plans/${id}/`, data);
    return response.data;
  },

  deleteMovementPlan: async (id: number): Promise<void> => {
    await api.delete(`/farmmap/movement-plans/${id}/`);
  },

  toggleMovementPlan: async (id: number): Promise<MovementPlan> => {
    const response = await api.post(`/farmmap/movement-plans/${id}/toggle_active/`);
    return response.data;
  },

  // Reports
  getUtilizationReport: async (startDate?: string, endDate?: string): Promise<PastureUtilizationReport> => {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/farmmap/utilization/', { params });
    return response.data;
  },

  // Historical Timeline
  getTimeline: async (params?: {
    year?: number;
    batch_id?: string;
    event_types?: string[];
  }): Promise<TimelineData> => {
    const queryParams: Record<string, string | number> = {};
    if (params?.year) queryParams.year = params.year;
    if (params?.batch_id) queryParams.batch_id = params.batch_id;
    if (params?.event_types && params.event_types.length > 0) {
      queryParams.event_types = params.event_types.join(',');
    }
    const response = await api.get('/farmmap/timeline/', { params: queryParams });
    return response.data;
  },

  getGrazingHistory: async (params?: {
    zone_id?: number;
    batch_id?: number;
    days?: number;
  }): Promise<GrazingSession[]> => {
    const response = await api.get('/farmmap/grazing-history/', { params });
    // Handle paginated response (returns { results: [...] }) or direct array
    const data = response.data;
    return Array.isArray(data) ? data : (data?.results || []);
  },

  // Laura AI Context
  getLauraContext: async (): Promise<LauraFarmContext> => {
    const response = await api.get('/farmmap/laura-context/');
    return response.data;
  },

  // Offline Sync
  syncOfflineActions: async (actions: Array<{
    action_type: string;
    queued_at: string;
    payload: Record<string, unknown>;
  }>): Promise<{ processed: number; results: Array<{ success: boolean; id?: number; error?: string }> }> => {
    const response = await api.post('/farmmap/sync/', { actions });
    return response.data;
  },

  // Tile Caching
  downloadTiles: async (minZoom?: number, maxZoom?: number): Promise<{
    bounds: {
      sw: { lat: number; lon: number };
      ne: { lat: number; lon: number };
    };
    min_zoom: number;
    max_zoom: number;
    tile_count: number;
    tiles: Array<{ z: number; x: number; y: number }>;
    mapbox_style: string;
  }> => {
    const data: Record<string, number> = {};
    if (minZoom !== undefined) data.min_zoom = minZoom;
    if (maxZoom !== undefined) data.max_zoom = maxZoom;
    const response = await api.post('/farmmap/tiles/download/', data);
    return response.data;
  },

  getTileCacheStatus: async (): Promise<{
    has_cache: boolean;
    cached_at: string | null;
    tile_count: number;
    size_mb: number;
    min_zoom: number;
    max_zoom: number;
    bounds: {
      sw: { lat: number | null; lon: number | null };
      ne: { lat: number | null; lon: number | null };
    } | null;
    tiles: Array<{ z: number; x: number; y: number; size: number; modified: string }>;
  }> => {
    const response = await api.get('/farmmap/tiles/status/');
    return response.data;
  },

  clearTileCache: async (): Promise<{ deleted: number }> => {
    const response = await api.delete('/farmmap/tiles/status/');
    return response.data;
  },

  // Voice Notes
  uploadVoiceNote: async (breadcrumbId: number, audioFile: File): Promise<{
    success: boolean;
    path?: string;
    url?: string;
    error?: string;
  }> => {
    const formData = new FormData();
    formData.append('breadcrumb_id', breadcrumbId.toString());
    formData.append('audio', audioFile);
    const response = await api.post('/farmmap/voice-notes/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getVoiceNoteUrl: async (breadcrumbId: number): Promise<{ url: string }> => {
    const response = await api.get(`/farmmap/voice-notes/${breadcrumbId}/`);
    return response.data;
  },

  // Session Archiving
  archiveSession: async (sessionId: number): Promise<{ success: boolean; path?: string; error?: string }> => {
    const response = await api.post(`/farmmap/sessions/${sessionId}/archive/`);
    return response.data;
  },

  // GeoJSON Export
  exportFarmGeoJSON: async (): Promise<{ success: boolean; path?: string; error?: string }> => {
    const response = await api.post('/farmmap/export-geojson/');
    return response.data;
  },

  // Planner Batches (from Farmhand planner)
  getBatches: async (params?: { status?: string }): Promise<PlannerBatch[]> => {
    const response = await api.get('/planner/portal/batches/', { params });
    // Handle paginated response or direct array
    const data = response.data;
    return Array.isArray(data) ? data : (data?.results || []);
  },

  // Upload cached tile to Spaces CDN (for auto-caching)
  uploadCachedTile: async (data: {
    z: number;
    x: number;
    y: number;
    tile_data: string; // base64 encoded
  }): Promise<{ success: boolean; path?: string; error?: string }> => {
    const response = await api.post('/farmmap/tiles/cache/', data);
    return response.data;
  },

  // Farm Config Sync (automatic - syncs on every zone/shelter/farm map change)
  // These methods are for manual sync trigger or status checks
  triggerSync: async (): Promise<{
    success: boolean;
    path?: string;
    saved_at?: string;
    error?: string;
  }> => {
    const response = await api.post('/farmmap/cloud-save/');
    return response.data;
  },

  loadFromCloud: async (): Promise<{
    success: boolean;
    config?: CloudBackupConfig;
    last_modified?: string;
    error?: string;
    not_found?: boolean;
  }> => {
    const response = await api.get('/farmmap/cloud-load/');
    return response.data;
  },

  getSyncStatus: async (): Promise<{
    exists: boolean;
    last_modified?: string;
    size_bytes?: number;
    storage_configured?: boolean;
  }> => {
    const response = await api.get('/farmmap/cloud-save/');
    return response.data;
  },

  // Incidents
  getIncidents: async (params?: {
    event_type?: string;
    severity?: string;
    resolved?: boolean;
    batch?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<FarmIncident[]> => {
    const response = await api.get('/farmmap/incidents/', { params });
    return response.data;
  },

  createIncident: async (data: {
    event_type: string;
    title: string;
    description?: string;
    coordinates?: { lat: number; lon: number };
    severity?: string;
    batch_id?: string;
    zone_id?: number;
    count?: number;
  }): Promise<FarmIncident> => {
    const response = await api.post('/farmmap/incidents/create/', data);
    return response.data;
  },

  getIncident: async (id: number): Promise<FarmIncident> => {
    const response = await api.get(`/farmmap/incidents/${id}/`);
    return response.data;
  },

  resolveIncident: async (id: number, notes: string = ''): Promise<FarmIncident> => {
    const response = await api.post(`/farmmap/incidents/${id}/resolve/`, { notes });
    return response.data;
  },

  // Pattern Detection
  getPatterns: async (params?: { days?: number }): Promise<PatternCluster[]> => {
    const response = await api.get('/farmmap/patterns/', { params });
    return response.data;
  },

  // Movement Plan Analysis
  getMovementPlanAnalysis: async (planId: number): Promise<MovementPlanAdvisory> => {
    const response = await api.get(`/farmmap/movement-plans/${planId}/analysis/`);
    return response.data;
  },

  evaluateZone: async (zoneId: number, animalType?: string): Promise<any> => {
    const response = await api.post(`/farmmap/zones/${zoneId}/evaluate/`, { animal_type: animalType });
    return response.data;
  },

  // Backwards compatibility aliases
  cloudSave: async () => farmMapAPI.triggerSync(),
  cloudLoad: async () => farmMapAPI.loadFromCloud(),
  getCloudBackupInfo: async () => farmMapAPI.getSyncStatus(),
};

export default farmMapAPI;

// ==== STANDALONE:FARMHAND_PRO:END ====
